import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { environment } from "../../../../environments/environment";
import { BehaviorSubject, map, Observable, Subject, tap } from "rxjs";
import { MessageService } from "primeng/api";
import { TranslateService } from '@ngx-translate/core';
import { Utility } from '../../core/utils/utility';

@Injectable({
  providedIn: 'root'
})
export class CourseService {

  orders: any = [];
  showBtnSubject: Subject<boolean> = new BehaviorSubject<boolean>(false);
  showBtn$: Observable<boolean> = this.showBtnSubject?.asObservable();

  countCartSubject: Subject<any> = new BehaviorSubject<any>([]);
  countCart$: Observable<any> = this.countCartSubject?.asObservable();

  isAcademySubject: Subject<boolean> = new BehaviorSubject<boolean>(false);
  isAcademy$: Observable<boolean> = this.isAcademySubject?.asObservable();

  constructor(private http: HttpClient,
    private messageService: MessageService,
    private translate: TranslateService
  ) {
    if (localStorage.getItem('orders')) {
      this.orders = this.orders.concat(JSON.parse(localStorage.getItem('orders') as string));
    } else {
      this.orders = [];
    }
    this.countCartSubject.next(this.orders);
  }

  isAcademy(value: boolean) {
    this.isAcademySubject?.next(value);
  }

  showBtn(value: boolean) {
    this.showBtnSubject.next(value);
  }

  addToCart(course: any) {
    if (this.orders.some((item: any) => item == course)) {
      this.messageService.add({ severity: 'warn', detail: this.translate.instant('PANEL.LMS.COURSE.ALREADY_IN_CART') });
      return;
    }
    this.orders.push(course);
    this.countCartSubject.next(this.orders);
    localStorage.setItem('orders', JSON.stringify(this.orders));
    this.messageService.add({ severity: 'success', detail: this.translate.instant('PANEL.LMS.COURSE.ADDED_TO_CART') });
  }

  removeFromCart(courseId: number) {
    let index = this.orders.findIndex((item: any) => item == courseId);
    this.orders.splice(index, 1);

    this.countCartSubject.next(this.orders);
    localStorage.setItem('orders', JSON.stringify(this.orders));
  }

  get(filter: null | any = null) {
    let params = new HttpParams();
    for (const key in filter) {
      if (filter[key])
        params = params.append(key, filter[key]);
    }

    return this.http.get(`${environment.apiUrl}/InnovationCourse`, { params: params })
  }

  getTitles() {
    return this.http.get(`${environment.apiUrl}/course/CourseTitle`)
  }

  add(value: any) {
    let formData = Utility.objectToFormData(value);

    let options = {
      headers: new HttpHeaders({
        "x-none": "true",
        "enctype": "multipart/form-data",
      }),
    };
    return this.http.post(`${environment.apiUrl}/InnovationCourse`, formData, options)
  }

  copy(courseId: number) {
    return this.http.post(`${environment.apiUrl}/InnovationCourse/copy`, { courseId })
  }

  getById(courseId: number, assignmentForUserIds: any = [], scopeId: string | null = null) {
    let params = new HttpParams();
    if (assignmentForUserIds?.length) {
      assignmentForUserIds.forEach((item: any) => {
        params = params.append('assignmentForUserIds', item);
      });
    }
    if (scopeId) {
      params = params.append('scopeId', scopeId);
    }
    return this.http.get(`${environment.apiUrl}/Course/GetById?Id=${courseId}`, { params: params });
  }

  getByTitle(courseTitle: number) {
    return this.http.get(`${environment.apiUrl}/Course/GetByTitle?title=${courseTitle}`);
  }

  getByIds(courseIds: any = []) {
    let params = new HttpParams();
    if (courseIds.length) {
      courseIds.forEach((item: any) => {
        params = params.append('Ids', item);
      });
    }

    return this.http.get(`${environment.apiUrl}/Course/GetByIds`, { params: params });
  }

  getForEdit(courseId: number) {
    return this.http.get(`${environment.apiUrl}/InnovationCourse/GetForEdit?Id=${courseId}`);
  }

  getStudentCourseStats(userIds: any = [], scopeId: string | null = null) {
    let params = new HttpParams();
    if (userIds?.length) {
      userIds.forEach((item: any) => {
        params = params.append('userIds', item);
      });
    }
    if (scopeId) {
      params = params.append('scopeId', scopeId);
    }
    return this.http.get(`${environment.apiUrl}/Course/getStudentCourseStats`, { params: params });
  }


  delete(courseId: number) {
    return this.http.delete(`${environment.apiUrl}/Course?Id=${courseId}`);
  }

  update(value: any) {
    let formData = Utility.objectToFormData(value);

    let options = {
      headers: new HttpHeaders({
        "x-none": "true",
        "enctype": "multipart/form-data",
      }),
    };

    return this.http.put(`${environment.apiUrl}/InnovationCourse`, formData, options)
  }

  getWithDetails(filter: any | null = null) {
    let params = new HttpParams();

    for (const key in filter) {
      if (filter[key])
        params = params.append(key, filter[key]);
    }

    return this.http.get(`${environment.apiUrl}/Course/WithDetails`, { params: params }).pipe(
      tap((res: any) => {
        res.result.forEach((course: any) => {
          course.teachers.forEach((x: any) => {
            if (x.profileImageUrl && !x.profileImageUrl.startsWith('http')) {
              x.profileImageUrl = environment.fileServerUrl + x.profileImageUrl;
            }
          });

        });
      })
    );
  }

  calculateCourse(value: any) {
    return this.http.post(`${environment.apiUrl}/Course/CalculatePromotion`, value)
  }

  getTopCourses() {
    return this.http.get(`${environment.apiUrl}/Course/GetTopCourses`);
  }

  buyCourse(value: any) {
    return this.http.post(`${environment.apiUrl}/Course/Buy`, value);
  }

  buyForStartupByAdmin(value: any) {
    return this.http.post(`${environment.apiUrl}/InnovationCourse/buyForStartupByAdmin`, value);
  }

  buyByAdmin(value: any) {
    return this.http.post(`${environment.apiUrl}/InnovationCourse/BuyByAdmin`, value);
  }

  removeBoughtCourseByAdmin(value: { courseId: any; userId: any; }) {
    return this.http.delete(`${environment.apiUrl}/InnovationCourse/RemoveBoughtCourseByAdmin`, { params: value });
  }

  removeStartupCourseByAdmin(value: { courseId: any; startupId: any; }) {
    return this.http.delete(`${environment.apiUrl}/InnovationCourse/RemoveStartupCourseByAdmin`, { params: value });
  }

  getBoutCourse(filter: any | null = null) {
    let params = new HttpParams();
    for (const key in filter) {
      if (filter[key])
        params = params.append(key, filter[key]);
    }

    return this.http.get(`${environment.apiUrl}/Course/GetBoughtCourses`, { params: params });
  }

  getBoutCourseForAdmin(filter: any | null = null) {
    let params = new HttpParams();
    for (const key in filter) {
      if (filter[key])
        params = params.append(key, filter[key]);
    }

    return this.http.get(`${environment.apiUrl}/InnovationCourse/GetBoughtCoursesForAdmin`, { params: params });
  }

  getStartupCourseForAdmin(tableState: any | null = null) {
    let params = Utility.tableStateToHttpParams(tableState);

    return this.http.get(`${environment.apiUrl}/InnovationCourse/GetstartupCoursesForAdmin`, { params: params });
  }

  getStartupCourses(startupId: any) {
    return this.http.get(`${environment.apiUrl}/InnovationCourse/GetstartupCourses`, { params: { startupId } });
  }

  getTotalInvoiceValues() {
    return this.http.get(`${environment.apiUrl}/Course/GetTotalInvoiceValues`);
  }

  courseInvoiceHasComplete(courseId: number) {
    return this.http.get(`${environment.apiUrl}/Course/CourseInvoiceHasComplete?Id=${courseId}`);
  }

  isUserBoughtCourse(courseId: number) {
    return this.http.get(`${environment.apiUrl}/Course/IsUserBoughtCourse?CourseId=${courseId}`);
  }

  getStudent(filter: any | null = null) {
    let params = new HttpParams();
    for (const key in filter) {
      if (filter[key])
        params = params.append(key, filter[key]);
    }

    return this.http.get(`${environment.apiUrl}/Course/GetStudents`, { params: params });
  }

  checkCertificate(verificationNo: number) {
    return this.http.get(`${environment.apiUrl}/Course/CheckCertificate?verificationNo=${verificationNo}`);
  }

  getCertificate(courseId: number) {
    return this.http.get(`${environment.apiUrl}/Course/GetCertificate?courseId=${courseId}`, { responseType: 'blob' }).pipe(map((res: any) => {
      return new Blob([res], { type: res.type });
    }));
  }

  getCertificateUrl(courseId: number) {
    return this.http.get(`${environment.apiUrl}/Course/GetCertificateUrl?courseId=${courseId}`);
  }

  getCertificateImageUrl(courseId: number) {
    return this.http.get(`${environment.apiUrl}/Course/GetCertificateImageUrl?courseId=${courseId}`);
  }

  getCertificateByVerificationCode(verificationCode: any) {
    return this.http.get(`${environment.apiUrl}/Course/GetCertificateByVerificationCode?verificationCode=${verificationCode}`, { responseType: 'blob' }).pipe(map((res: any) => {
      return new Blob([res], { type: res.type });
    }));
  }
}
